import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@codes/ui-kit/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { WorkspaceStereo, WorkspaceView } from '@lemoncloud/codes-goods-api';
import { getWorkspaceBadge, useDialogStore } from '@web/app/shared';
import { Check, ChevronDown, ChevronRight, Home } from 'lucide-react';
import moment from 'moment';

import { Dialog, DialogContent, DialogHeader, DialogTitle, Skeleton } from '@codes/ui-kit';
import { isProdEnvironment } from '@codes/web-core';
import { useMyWorkspaces } from '@codes/workspace';

export const SubscriptionsPage = () => {
    const isProd = isProdEnvironment();
    const expandedNumber = 2;
    const { t } = useTranslation();
    const myWorkspaces = useMyWorkspaces();

    const [isExpanded, setIsExpanded] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);

    const { openPriceDialog } = useDialogStore(state => state);

    const [workspace, setWorkspace] = useState<WorkspaceView | null>(null);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const haveWorkspaces = (myWorkspaces.data?.total || 0) > 0;
    const [spaceStereo, setSpaceStereo] = useState<WorkspaceStereo | 'all'>('all');

    const handleStereoChange = (newStereo: WorkspaceStereo | 'all') => {
        setIsExpanded(false);
        setSpaceStereo(newStereo);
    };

    const handleWorkspaceClick = (workspace: WorkspaceView | undefined) => {
        if (!workspace) {
            return;
        }
        setWorkspace(workspace);
        setDetailOpen(true);
    };

    const filteredWorkspaces = useMemo(() => {
        if (!myWorkspaces || !myWorkspaces.data || !myWorkspaces.data.data) {
            return [];
        }
        const workspaces = myWorkspaces.data.data;

        switch (spaceStereo) {
            case 'all':
                return workspaces;
            case 'pro':
                return workspaces.filter(workspace => workspace.stereo === 'pro');
            default:
                return workspaces.filter(workspace => workspace.stereo !== 'pro');
        }
    }, [myWorkspaces, spaceStereo]);

    const slicedWorkspaces = useMemo(() => {
        return isExpanded ? filteredWorkspaces : filteredWorkspaces.slice(0, expandedNumber);
    }, [isExpanded, filteredWorkspaces]);

    return (
        <div className="max-w-2xl mx-auto">
            <div className="w-full flex items-center justify-between mb-4">
                <div className="text-sm font-semibold text-text uppercase tracking-wider">
                    {t('myPage.subscriptionInfo')}
                </div>
                <button className="text-sm font-medium text-sub-point flex items-center" onClick={openPriceDialog}>
                    {t('myPage.viewAllPlans')}
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>

            <div className="flex flex-col space-y-5 mt-4">
                {myWorkspaces.isLoading ? (
                    <div className="flex flex-col gap-y-2">
                        <Skeleton className="h-10 rounded-lg mb-4" />
                        <Skeleton className="h-28 rounded-lg" />
                        <Skeleton className="h-28 rounded-lg" />
                        <Skeleton className="h-28 rounded-lg" />
                        <Skeleton className="h-28 rounded-lg" />
                    </div>
                ) : (
                    <>
                        <Select value={spaceStereo} onValueChange={handleStereoChange}>
                            <SelectTrigger className="bg-card border-none shadow-custom font-medium">
                                <SelectValue placeholder="" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('myPage.all')}</SelectItem>
                                {haveWorkspaces ? (
                                    <>
                                        <SelectItem value="basic">Basic Plan</SelectItem>
                                        <SelectItem value="pro">Pro Plan</SelectItem>
                                    </>
                                ) : (
                                    <SelectItem value="-" disabled>
                                        -
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                        {filteredWorkspaces.length === 0 ? (
                            <div className="w-full text-sm text-sub-title text-center my-10">{t('myPage.noData')}</div>
                        ) : (
                            slicedWorkspaces.map(workspace => (
                                <div
                                    key={workspace.id}
                                    className="rounded-xl border border-border bg-card p-5 flex items-center gap-4 cursor-pointer hover:bg-box transition-colors"
                                    onClick={() => handleWorkspaceClick(workspace)}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-point/10 flex items-center justify-center shrink-0">
                                        <Home className="w-5 h-5 text-point" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm flex items-center gap-2">
                                            {workspace.name}
                                            {getWorkspaceBadge(workspace.stereo)}
                                        </div>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-text-400 shrink-0" />
                                </div>
                            ))
                        )}
                    </>
                )}
            </div>

            <div className="flex justify-center mt-5">
                {expandedNumber < filteredWorkspaces.length && (
                    <button
                        className="flex-1 text-xs text-sub-title flex justify-center items-center gap-1"
                        onClick={toggleExpand}
                    >
                        {isExpanded ? t('myPage.collapse') : t('myPage.viewMore')}
                        <ChevronDown className={`w-4 h-4 ${isExpanded ? 'rotate-180' : ''} `} />
                    </button>
                )}
            </div>
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="max-w-[500px] p-0" aria-describedby={undefined}>
                    {workspace && (
                        <div className="h-full">
                            <DialogHeader>
                                <DialogTitle className="text-base">{t('myPage.subscriptionInfo')}</DialogTitle>
                            </DialogHeader>
                            <div className="p-6 h-[calc(100%-52px)] overflow-auto flex flex-col">
                                <div className="text-lg font-medium flex items-center gap-1">
                                    {workspace?.name}
                                    {getWorkspaceBadge(workspace?.stereo)}
                                </div>
                                <div className="flex items-center justify-between text-sm mt-3">
                                    <div className="text-sub-title">{t('myPage.createdAt')}</div>
                                    <div className="font-medium">
                                        {moment(workspace.createdAt).format('YYYY. MM. DD')}
                                    </div>
                                </div>
                                <div className="my-4 border-b border-divider"></div>
                                <div className="text-lg font-medium mb-2">{t('myPage.subscriptionInfo')}</div>
                                <div className="flex flex-col gap-2">
                                    <Card className="bg-box py-4 px-5 border border-detail-line rounded-2xl flex flex-col gap-2">
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center justify-between text-base font-medium">
                                                <div>{workspace.stereo === 'pro' ? 'Pro' : 'Basic'}</div>
                                                <div>$0</div>
                                            </div>
                                            <div className="border-b border-divider"></div>
                                            <div className="flex items-center gap-2">
                                                <Check className="w-4 h-4 text-point" />
                                                <div className="text-sm">{t('subscription.allBasicFeatures')}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Check className="w-4 h-4 text-point" />
                                                <div className="text-sm">{t('subscription.10HoursSupport')}</div>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                                {!isProd && (
                                    <>
                                        <div className="my-4 border-b border-divider"></div>

                                        <div className="text-lg font-medium mb-1.5">{t('myPage.paymentHistory')}</div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex flex-col gap-2">
                                                <div className="flex items-center justify-between text-sm">
                                                    <div className="text-sub-title">{t('myPage.paymentContent')}</div>
                                                    <div className="font-medium">Pro Plan 구독(정기) $00</div>
                                                </div>
                                                <div className="flex items-center justify-between text-sm">
                                                    <div className="text-sub-title">{t('myPage.paymentDate')}</div>
                                                    <div className="font-medium">2024. 10. 01</div>
                                                </div>
                                                <div className="flex items-center justify-between text-sm">
                                                    <div className="text-sub-title">{t('myPage.paymentMethod')}</div>
                                                    <div className="font-medium">삼성카드(123*)</div>
                                                </div>
                                                <div className="flex items-center justify-between text-sm">
                                                    <div className="text-sub-title">{t('myPage.status')}</div>
                                                    <div className="font-medium text-error">
                                                        {t('myPage.paymentUnavailable')}
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between text-lg font-medium">
                                                    <div>{t('myPage.paymentAmount')}</div>
                                                    <div>$0</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex justify-center mt-6">
                                            <button className="text-sm text-sub-title font-medium hover:underline">
                                                {t('myPage.cancelRequest')}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
